import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import { Checkbox, Typography } from '@material-ui/core';
import LayersIcon from '@material-ui/icons/Layers';
import { RMSButton } from '../../../shared/components/buttons';
import { RMSTextField } from '../../../shared/components/form-controls';
import RMSChip from '../../../shared/components/chips/RMSChip';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import Evidence_PERMISSIONS from '../../constants/evidencePermissionsConstants';
import { normalizeStatus, getStatusChipClass } from '../../constants/evidenceGridColumns';
import CheckOutRequestDialog from '../add-edit-evidence/CheckOutRequestDialog';
import { createEvidenceCheckoutRequest } from '../../services/evidenceService';
import { formatTime } from '../../../shared/utility/dateFormatter';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import useDebouncedValue from '../../../shared/utility/useDebouncedValue';
import { getRowDetails } from '../../utility/evidencePortalHelper';
import EvidencePortalStatTiles from './EvidencePortalStatTiles';
import { DEFAULT_PAGE_SIZE } from '../../constants/evidenceRecordsFilters';

const EvidencePortalMyEvidenceTable = ({ agencyId, summary, evidenceRecords, onOpenEvidence }) => {
  const dispatch = useDispatch();
  const checkoutPermission = getFullPermissions(
    'evidence',
    Evidence_PERMISSIONS.Check_Out_Requests,
    'any'
  );
  const canRequestCheckout = checkoutPermission?.Create ?? false;
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const searchMethods = useForm({ mode: 'onChange', defaultValues: { searchText: '' } });
  const searchInput = useWatch({ control: searchMethods.control, name: 'searchText' });
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, evidenceRecords.tileFilter]);

  useEffect(() => {
    evidenceRecords.handleGridStateChange({
      searchText: debouncedSearch,
      page,
      pageSize: DEFAULT_PAGE_SIZE,
      sortModel: [],
    });
  }, [debouncedSearch, page, evidenceRecords.handleGridStateChange]);

  useEffect(() => {
    setSelected(new Set());
  }, [evidenceRecords.items]);

  const items = evidenceRecords.items;
  const total = evidenceRecords.total;
  const allSelected = items.length > 0 && items.every((item) => selected.has(item.ptsEvidenceID));
  const selectedItems = useMemo(() => items.filter((item) => selected.has(item.ptsEvidenceID)), [
    items,
    selected,
  ]);

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((item) => item.ptsEvidenceID)));
    }
  };

  const toggleOne = (ptsEvidenceID) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(ptsEvidenceID)) next.delete(ptsEvidenceID);
      else next.add(ptsEvidenceID);
      return next;
    });
  };

  const handleSubmitCheckoutRequest = async (data) => {
    await executeApiAction({
      setLoading: setCheckoutSaving,
      dispatch,
      action: () =>
        Promise.all(
          data.ptsEvidenceIDs.map((ptsEvidenceID) =>
            createEvidenceCheckoutRequest({
              AgencyID: agencyId,
              RequestByPersonID: data.requestedByObject,
              ptsEvidenceID,
              PickupLocationID: data.pickupLocation,
              PickupDate: data.pickupDate,
              PickupTime: formatTime(data.pickupTime),
              ReasonNeededID: data.reason,
              Comments: data.comments,
              IsDeleted: false,
            })
          )
        ),
      onSuccess: () => {
        dispatch(notify('Checkout request submitted successfully.', 'success'));
        setSelected(new Set());
      },
    });
  };

  const from = total === 0 ? 0 : page * DEFAULT_PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * DEFAULT_PAGE_SIZE);

  return (
    <div className="evidence-portal-my-evidence">
      <EvidencePortalStatTiles
        summary={summary}
        tileFilter={evidenceRecords.tileFilter}
        onFilterChange={evidenceRecords.applyTileFilter}
      />

      <FormProvider {...searchMethods}>
        <div className="evidence-portal-search">
          <RMSTextField name="searchText" label="Search my evidence..." />
        </div>
      </FormProvider>

      {canRequestCheckout && selected.size > 0 && (
        <div className="evidence-portal-selection-bar">
          <Typography variant="body2">
            <strong>{selected.size}</strong> item{selected.size > 1 ? 's' : ''} selected
          </Typography>
          <RMSButton
            label="Request Checkout"
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setCheckoutDialogOpen(true)}
          />
          <RMSButton
            label="Clear"
            variant="outlined"
            size="small"
            onClick={() => setSelected(new Set())}
          />
        </div>
      )}

      <div className="sectionBar sectionBar--noShrink">
        <div className="sectionLabel">
          <span className="sectionLabelIcon">
            <LayersIcon className="sectionLabelIconSvg" />
          </span>
          My Evidence
          <span className="sectionBadge">{total}</span>
        </div>
      </div>

      {!items.length ? (
        <div className="evidence-portal-empty">No evidence items found.</div>
      ) : (
        <div className="evidence-portal-table-wrapper">
          <table className="evidence-portal-table">
            <thead>
              <tr>
                {canRequestCheckout && (
                  <th style={{ width: 36 }}>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      onChange={toggleAll}
                      color="primary"
                    />
                  </th>
                )}
                <th>Evidence ID</th>
                <th>Incident</th>
                <th>Description</th>
                <th>Status</th>
                <th>Storage</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const statusLabel = normalizeStatus(item.Status) || 'Waiting';
                return (
                  <tr key={item.ptsEvidenceID}>
                    {canRequestCheckout && (
                      <td onClick={(event) => event.stopPropagation()}>
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={selected.has(item.ptsEvidenceID)}
                          onChange={() => toggleOne(item.ptsEvidenceID)}
                        />
                      </td>
                    )}
                    <td
                      className="evidence-portal-table__eid is-clickable"
                      onClick={() => onOpenEvidence(item.ptsEvidenceID)}>
                      {item.EvidenceID}
                    </td>
                    <td className="is-clickable" onClick={() => onOpenEvidence(item.ptsEvidenceID)}>
                      {item.IncidentNumber || item.incidentReportID || '—'}
                    </td>
                    <td className="is-clickable" onClick={() => onOpenEvidence(item.ptsEvidenceID)}>
                      {(item.Description || '').slice(0, 60)}
                    </td>
                    <td onClick={() => onOpenEvidence(item.ptsEvidenceID)}>
                      <RMSChip
                        label={statusLabel}
                        size="small"
                        className={`evidence-chip evidence-chip--status ${getStatusChipClass(
                          statusLabel
                        )}`}
                      />
                    </td>
                    <td className="is-clickable" onClick={() => onOpenEvidence(item.ptsEvidenceID)}>
                      {item.CurrentLocation || '—'}
                    </td>
                    <td className="is-clickable" onClick={() => onOpenEvidence(item.ptsEvidenceID)}>
                      {getRowDetails(item)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="evidence-portal-pagination">
            <span>
              {from}–{to} of {total}
            </span>
            <RMSButton
              label="Prev"
              variant="outlined"
              size="small"
              disabled={page === 0}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            />
            <RMSButton
              label="Next"
              variant="outlined"
              size="small"
              disabled={to >= total}
              onClick={() => setPage((current) => current + 1)}
            />
          </div>
        </div>
      )}

      {checkoutDialogOpen && (
        <CheckOutRequestDialog
          open={checkoutDialogOpen}
          ptsEvidences={selectedItems}
          loading={checkoutSaving}
          onSubmit={handleSubmitCheckoutRequest}
          onClose={() => setCheckoutDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default EvidencePortalMyEvidenceTable;
